import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Link } from '@tiptap/extension-link';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon,
  List, 
  ListOrdered,
  Heading1,
  Heading2,
  Link as LinkIcon,
  Palette,
  X
} from 'lucide-react';

/**
 * Rich Text Editor component with formatting options using Tiptap
 * Supports bold, italic, underline, lists, links, headings, and font colors
 * @param {boolean} compact - If true, shows only B, I, U, and color (for titles)
 */
export const RichTextEditor = ({ value, onChange, placeholder = 'Escribe aquí...', compact = false }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // Paleta de colores predefinida
  const colors = [
    { name: 'Negro', hex: '#000000' },
    { name: 'Gris oscuro', hex: '#374151' },
    { name: 'Rojo', hex: '#DC2626' },
    { name: 'Naranja', hex: '#EA580C' },
    { name: 'Amarillo', hex: '#CA8A04' },
    { name: 'Verde', hex: '#16A34A' },
    { name: 'Azul', hex: '#2563EB' },
    { name: 'Morado', hex: '#9333EA' },
    { name: 'Rosa', hex: '#DB2777' },
    { name: 'Marrón', hex: '#92400E' },
  ];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: compact ? false : {
          levels: [1, 2, 3],
        },
        bulletList: !compact,
        orderedList: !compact,
      }),
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none ${compact ? 'min-h-[60px]' : 'min-h-[150px]'} p-3`,
      },
    },
  });

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const url = window.prompt('URL del enlace:', '');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const applyColor = (color) => {
    editor.chain().focus().setColor(color).run();
    setShowColorPicker(false);
  };

  return (
    <div className="rich-text-editor border border-slate-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-slate-50 border-b border-slate-300 relative">
        {/* Headings - Solo si no es compacto */}
        {!compact && (
          <>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={`p-2 rounded hover:bg-slate-200 transition-colors ${
                editor.isActive('heading', { level: 1 }) ? 'bg-slate-300 font-bold' : ''
              }`}
              title="Título 1"
            >
              <Heading1 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={`p-2 rounded hover:bg-slate-200 transition-colors ${
                editor.isActive('heading', { level: 2 }) ? 'bg-slate-300 font-bold' : ''
              }`}
              title="Título 2"
            >
              <Heading2 className="w-4 h-4" />
            </button>
            <div className="w-px h-6 bg-slate-300 mx-1"></div>
          </>
        )}

        {/* Text formatting */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-slate-200 transition-colors ${
            editor.isActive('bold') ? 'bg-slate-300 font-bold' : ''
          }`}
          title="Negrita"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-slate-200 transition-colors ${
            editor.isActive('italic') ? 'bg-slate-300 font-bold' : ''
          }`}
          title="Cursiva"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-slate-200 transition-colors ${
            editor.isActive('underline') ? 'bg-slate-300 font-bold' : ''
          }`}
          title="Subrayado"
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-slate-300 mx-1"></div>

        {/* Color */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            className={`p-2 rounded hover:bg-slate-200 transition-colors ${
              showColorPicker ? 'bg-slate-300' : ''
            }`}
            title="Color de texto"
          >
            <Palette className="w-4 h-4" />
          </button>
          
          {/* Color Picker Dropdown */}
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-slate-300 rounded-lg shadow-lg p-3 z-10 min-w-[280px]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Selecciona un color</span>
                <button
                  type="button"
                  onClick={() => setShowColorPicker(false)}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {colors.map((color) => (
                  <button
                    key={color.hex}
                    type="button"
                    onClick={() => applyColor(color.hex)}
                    className="flex flex-col items-center gap-1 p-2 rounded hover:bg-slate-50 transition-colors"
                    title={color.name}
                  >
                    <div
                      className="w-8 h-8 rounded border-2 border-slate-200"
                      style={{ backgroundColor: color.hex }}
                    ></div>
                    <span className="text-xs text-slate-600">{color.hex}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Listas y enlaces - Solo si no es compacto */}
        {!compact && (
          <>
            <div className="w-px h-6 bg-slate-300 mx-1"></div>

            {/* Lists */}
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-2 rounded hover:bg-slate-200 transition-colors ${
                editor.isActive('bulletList') ? 'bg-slate-300 font-bold' : ''
              }`}
              title="Lista con viñetas"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`p-2 rounded hover:bg-slate-200 transition-colors ${
                editor.isActive('orderedList') ? 'bg-slate-300 font-bold' : ''
              }`}
              title="Lista numerada"
            >
              <ListOrdered className="w-4 h-4" />
            </button>

            <div className="w-px h-6 bg-slate-300 mx-1"></div>

            {/* Link */}
            <button
              type="button"
              onClick={setLink}
              className={`p-2 rounded hover:bg-slate-200 transition-colors ${
                editor.isActive('link') ? 'bg-slate-300 font-bold' : ''
              }`}
              title="Añadir enlace"
            >
              <LinkIcon className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Editor */}
      <EditorContent editor={editor} className="bg-white" />

      <style>{`
        .ProseMirror {
          min-height: ${compact ? '60px' : '150px'};
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: "${placeholder}";
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror h1 {
          font-size: 2em;
          font-weight: bold;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        .ProseMirror h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        .ProseMirror ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ProseMirror ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ProseMirror li {
          margin: 0.25em 0;
        }
      `}</style>
    </div>
  );
};
